import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import {
  Button,
  CalendarMinus01,
  DotsVertical,
  Edit01,
  EmptyState,
  Menu,
  MessageXSquare,
  Modal,
  PageHeader,
  Pagination,
  Plus,
  SearchLg,
  Settings01,
  Table,
  Tabs,
  TextInput,
  Trash01,
} from '@opendate-app/setlist';
import SocialPostStatusBars from '../../SocialPostStatusBars/components/SocialPostStatusBars';
import { toastError } from '../../../shared/toastHelper';

const axios = require('axios').default;
const qs = require('qs');

const PAGE_SIZE = 25;

const VIEW_COLUMNS = {
  scheduled: [
    { data: 'json', searchable: false, orderable: false },
    { data: 'date_time', searchable: false, orderable: true },
    { data: 'post', searchable: true, orderable: true },
    { data: 'image', searchable: false, orderable: false },
    { data: 'channels', searchable: false, orderable: false },
    { data: 'event', searchable: true, orderable: true },
    { data: 'manage', searchable: false, orderable: false },
  ],
  posted: [
    { data: 'json', searchable: false, orderable: false },
    { data: 'date_time', searchable: false, orderable: true },
    { data: 'post', searchable: true, orderable: true },
    { data: 'image', searchable: false, orderable: false },
    { data: 'event', searchable: true, orderable: true },
    { data: 'manage', searchable: false, orderable: false },
  ],
};

const DEFAULT_DIR = { scheduled: 'asc', posted: 'desc' };

const CHANNEL_ICONS = {
  facebook: 'fab fa-facebook-f',
  twitter: 'fab fa-twitter',
  instagram: 'fab fa-instagram',
};

const CHANNEL_LABELS = { facebook: 'Facebook', twitter: 'Twitter', instagram: 'Instagram' };

const cardStyle = {
  marginTop: 24,
  background: 'var(--sl-bg-primary)',
  borderRadius: '12px',
  boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
  border: '1px solid var(--sl-border-secondary)',
};

const thumbStyle = { width: 40, height: 40, borderRadius: 6, objectFit: 'cover', flexShrink: 0 };
const bodyStyle = { overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' };

export const buildParams = (view, page, dir, search) => {
  const columns = VIEW_COLUMNS[view];
  return {
    draw: page,
    start: (page - 1) * PAGE_SIZE,
    length: PAGE_SIZE,
    search: { value: search, regex: false },
    columns: Object.fromEntries(columns.map((column, index) => [index, {
      data: column.data,
      name: '',
      searchable: column.searchable,
      orderable: column.orderable,
      search: { value: '', regex: false },
    }])),
    order: { 0: { column: columns.findIndex((column) => column.data === 'date_time'), dir } },
  };
};

const formatDateTime = (iso) => (
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—'
);

const SocialPostsList = ({
  view,
  dataSource,
  team,
  currentUser,
  authorizedPages,
  hasPosts,
  onNewPost,
  onEditPost,
  onRemovePost,
  reloadKey,
}) => {
  const [page, setPage] = useState(1);
  const [dir, setDir] = useState(DEFAULT_DIR[view]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [postToRemove, setPostToRemove] = useState(null);
  const latestRequest = useRef(0);

  const isScheduled = view === 'scheduled';
  const canManage = !!currentUser.can_manage_posts;
  const hasAccounts = authorizedPages.length > 0;

  useEffect(() => {
    if (searchInput === search) return undefined;
    const handle = setTimeout(() => {
      unstable_batchedUpdates(() => {
        setSearch(searchInput);
        setPage(1);
      });
    }, 300);
    return () => clearTimeout(handle);
  }, [searchInput, search]);

  useEffect(() => {
    if (!hasAccounts) return;
    const requestId = ++latestRequest.current;
    setLoading(true);
    axios.get(dataSource, {
      params: buildParams(view, page, dir, search),
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'brackets' }),
      headers: { Accept: 'application/json' },
    }).then(({ data }) => {
      if (requestId !== latestRequest.current) return;
      const lastPage = Math.max(1, Math.ceil((data.recordsFiltered || 0) / PAGE_SIZE));
      if (page > lastPage) {
        setPage(lastPage);
        return;
      }
      setRows(data.data || []);
      setTotal(data.recordsFiltered || 0);
      setLoaded(true);
    }).catch(() => {
      if (requestId === latestRequest.current) toastError('Could not load posts, please try again');
    }).finally(() => {
      if (requestId === latestRequest.current) setLoading(false);
    });
  }, [view, dataSource, page, dir, search, reloadKey, hasAccounts]);

  const goTo = (path) => window.location.assign(path);
  const composeNew = () => (isScheduled ? onNewPost() : goTo(`/teams/${team.id}/posts/scheduled?onp=true`));

  const handleTab = (value) => {
    if (value !== view) goTo(`/teams/${team.id}/posts/${value}`);
  };

  const confirmRemove = () => {
    const post = postToRemove;
    setPostToRemove(null);
    onRemovePost(post.post);
  };

  const columns = [
    {
      key: 'date_time',
      header: 'Date/Time',
      sortable: true,
      width: '180px',
      render: (row) => formatDateTime(isScheduled ? row.scheduled_at : row.published_at || row.scheduled_at),
    },
    {
      key: 'post',
      header: 'Post',
      primary: true,
      render: (row) => (
        <div>
          <div className="d-flex align-items-center" style={{ gap: 12 }}>
            {row.image_url ? <img src={row.image_url} alt="" style={thumbStyle} /> : null}
            <span style={bodyStyle}>{row.body}</span>
          </div>
          {!isScheduled && row.social_posts && row.social_posts.length > 0 ? (
            <div style={{ marginTop: 8 }}>
              <SocialPostStatusBars post={{ social_posts: row.social_posts }} />
            </div>
          ) : null}
        </div>
      ),
    },
    ...(isScheduled ? [{
      key: 'channels',
      header: 'Channels',
      width: '120px',
      render: (row) => (
        <span className="d-inline-flex" style={{ gap: 10 }}>
          {row.channels.map((channel) => (
            <i key={channel} className={`${CHANNEL_ICONS[channel]} fa-fw`} role="img" aria-label={CHANNEL_LABELS[channel]} title={CHANNEL_LABELS[channel]} />
          ))}
        </span>
      ),
    }] : []),
    {
      key: 'event',
      header: 'Event',
      render: (row) => {
        if (!row.event) return '—';
        return row.event.path
          ? <a href={row.event.path} style={{ color: 'var(--sl-text-brand)' }}>{row.event.title}</a>
          : row.event.title;
      },
    },
    ...(canManage ? [{
      key: 'actions',
      header: '',
      align: 'right',
      width: '48px',
      render: (row) => (
        <Menu
          placement="bottom-end"
          trigger={<Button variant="tertiary" size="sm" icon={DotsVertical} aria-label="More actions for post" />}
          items={[
            ...(isScheduled && row.can_edit ? [{ label: 'Edit post details', icon: Edit01, onClick: () => onEditPost(row.post) }] : []),
            { label: 'Remove', icon: Trash01, destructive: true, onClick: () => setPostToRemove(row) },
          ]}
        />
      ),
    }] : []),
  ];

  const noPosts = !hasPosts || (loaded && total === 0 && search === '');
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const showToolbarSearch = hasAccounts && !noPosts;

  const emptyState = (() => {
    if (!hasAccounts) {
      return (
        <EmptyState
          icon={Settings01}
          title="Set up Social Accounts"
          description="Your social accounts aren’t set up yet. Configure your accounts in order to schedule social posts."
          primaryAction={currentUser.can_manage_authorized_pages ? { label: 'Configure Social Accounts', as: 'a', href: '/integrations' } : undefined}
        />
      );
    }
    if (noPosts) {
      return (
        <EmptyState
          icon={isScheduled ? CalendarMinus01 : MessageXSquare}
          title={isScheduled ? 'No Posts Scheduled' : 'Nothing Posted Yet'}
          description={isScheduled ? 'Once you schedule a post it will show up here.' : 'Already posted social posts will be listed here.'}
          primaryAction={canManage ? { label: 'Schedule a Post', onClick: composeNew } : undefined}
        />
      );
    }
    return null;
  })();

  return (
    <div>
      <PageHeader
        title="Social Media"
        actions={hasAccounts && canManage ? (
          <Button variant="primary" icon={Plus} onClick={composeNew} data-testid="header-new-post">New Post</Button>
        ) : null}
      />
      <div className="overflow-hidden" style={cardStyle}>
        <div className="px-4 py-3 d-flex flex-wrap align-items-center" style={{ gap: 12 }}>
          <Tabs
            variant="button-border"
            size="sm"
            collapseBelow={0}
            items={[{ value: 'scheduled', label: 'Scheduled' }, { value: 'posted', label: 'Posted' }]}
            value={view}
            onChange={handleTab}
          />
          {showToolbarSearch ? (
            <>
              <div style={{ width: 280 }}>
                <TextInput
                  size="sm"
                  leadingIcon={SearchLg}
                  placeholder="Search"
                  aria-label="Search posts"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                />
              </div>
              <span className="figma-text sm" style={{ color: 'var(--sl-text-tertiary)' }}>
                {`${total.toLocaleString()} ${total === 1 ? 'post' : 'posts'}`}
              </span>
            </>
          ) : null}
        </div>

        {emptyState ? (
          <div style={{ padding: '24px 8px 48px' }}>{emptyState}</div>
        ) : (
          <>
            <div style={{ padding: '4px 8px' }}>
              <Table
                columns={columns}
                data={rows}
                getRowKey={(row) => row.id}
                loading={loading}
                loadingLabel="Loading posts"
                stackBelow={640}
                sort={{ key: 'date_time', direction: dir }}
                onSortChange={(_key, direction) => unstable_batchedUpdates(() => { setDir(direction); setPage(1); })}
                empty={<EmptyState size="sm" icon={SearchLg} title="No posts found" description="Try a different search." />}
              />
            </div>
            {totalPages > 1 && (
              <div className="px-4 py-3 border-top">
                <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
              </div>
            )}
          </>
        )}
      </div>

      <Modal
        open={!!postToRemove}
        onClose={() => setPostToRemove(null)}
        type="error"
        title="Remove post?"
        description="You're about to remove this post. This cannot be undone."
        footer={(
          <>
            <Button variant="secondary" onClick={() => setPostToRemove(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmRemove}>Remove</Button>
          </>
        )}
      />
    </div>
  );
};

SocialPostsList.propTypes = {
  view: PropTypes.oneOf(['scheduled', 'posted']).isRequired,
  dataSource: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  authorizedPages: PropTypes.array,
  hasPosts: PropTypes.bool,
  onNewPost: PropTypes.func,
  onEditPost: PropTypes.func,
  onRemovePost: PropTypes.func,
  reloadKey: PropTypes.number,
};

SocialPostsList.defaultProps = {
  authorizedPages: [],
  hasPosts: false,
  onNewPost: () => {},
  onEditPost: () => {},
  onRemovePost: () => {},
  reloadKey: 0,
};

export default SocialPostsList;
